import type { SignupStep } from './utils/steps';

/**
 * How the funnel asks for a step change. Ordinary advances push a history
 * entry so Back walks the funnel; a funnel-integrity bounce passes
 * `replace: true` because it must *not* stack on top of the URL it rejects —
 * otherwise Back returns to the disallowed step, the guard bounces again, and
 * the entry before the funnel becomes unreachable.
 */
export type NavigateToSignupStep = (step: SignupStep, options?: { replace?: boolean }) => void;

export type SignupPageProps = {
  step: SignupStep;
  /** Steps navigate the funnel through this; the URL stays the source of truth. */
  onNavigateStep: NavigateToSignupStep;
  /** Which product the visitor is signing up for (`?signupType=`). Consumed by the flow state. */
  signupType: string | undefined;
  /** Promo code carried in the URL (`?promoCode=`). Consumed by the flow state. */
  promoCode: string | undefined;
  /**
   * Seed for the flow's form state. The entry route prefilled
   * `email`/`first`/`last` from the marketing site's query params — the entry
   * step lands that prefill through this. Tests use it to start past the
   * empty-email funnel guard.
   */
  initialFormValues?: Partial<SignupFormValues>;
};

/**
 * The signup form's accumulated answers. Page-local state — no account or
 * user exists until the final register call pushes one.
 *
 * ⚠️ "Unset" is the empty string for every string field — including
 * `dataCenter` and `startupSize`, which historically defaulted to `undefined`.
 * Step UIs gating on an answer must check `=== ''`, never `== null`.
 */
export type SignupFormValues = {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  passwordConfirmation: string;
  companyName: string;
  role: string;
  signupType: string;
  companyUrl: string;
  noWebsite: boolean;
  acceptTerms: boolean;
  phone: string;
  phoneCountry: string;
  startupSize: string;
  startupFunding: string;
  incubatorVc: string;
  companyLocation: string;
  heardAboutUs: string;
  businessDescription: string;
  dataCenter: string;
  isGoogleSignup: boolean | undefined;
  signupVerificationCode: string;
  useCase: string;
  useCaseDetail: string;
  workPhone: string;
  employeeCount: string;
  cdpPlatform: string;
  cdpPlatformOther: string;
  channels: string[];
};

/** Matches the default phone country (`utils/phone-country-codes.ts`). */
export const DEFAULT_PHONE_COUNTRY = 'US';

export function createDefaultSignupFormValues(): SignupFormValues {
  return {
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    passwordConfirmation: '',
    companyName: '',
    role: '',
    signupType: 'journeys',
    companyUrl: '',
    noWebsite: false,
    acceptTerms: true,
    phone: '',
    phoneCountry: DEFAULT_PHONE_COUNTRY,
    startupSize: '',
    startupFunding: '',
    incubatorVc: '',
    companyLocation: '',
    heardAboutUs: '',
    businessDescription: '',
    dataCenter: '',
    isGoogleSignup: undefined,
    signupVerificationCode: '',
    useCase: '',
    useCaseDetail: '',
    workPhone: '',
    employeeCount: '',
    cdpPlatform: '',
    cdpPlatformOther: '',
    channels: [],
  };
}
